import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { FormattedMessage, injectIntl } from 'react-intl';
import TagsInput from 'react-tagsinput';
import 'react-tagsinput/react-tagsinput.css';
import {
  Row, Col, Button, Label, Card, CardHeader, CardBody, Form, FormGroup, Input, Alert,
} from 'reactstrap';
import Unregister from './Unregister';
import { get, update, setProfileError } from '../../actions/user';
import { uploadUserImage, removeUserImage } from '../../utils/Api';
import { getErrorMessageKey } from '../../utils/helper';

const filesSizeLimit = 5242880; // 5 MB

const mapStateToProps = ({ user: { permissions, profile }, i18n: { translations } }) => ({
  permissions: permissions || [],
  profile: profile || {},
  translations,
});

const mapDispatchToProps = dispatch => bindActionCreators({
  getAction: get,
  updateAction: update,
  setError: setProfileError,
}, dispatch);

const Profile = ({ intl, permissions, profile, translations, getAction, updateAction, setError }) => {
  const [state, setState] = useState({});
  const set = fields => setState(rest => ({ ...rest, ...fields }));
  const resetError = () => {
    if (profile && profile.message) setError('');
  };
  useEffect(() => {
    resetError();
    getAction();
  }, []);
  useEffect(() => {
    if (profile) set(profile);
  }, [profile]);

  if (!permissions.includes('LEARNING_PROFILE') || !state.id) {
    return null;
  }

  const fileUploaded = ev => {
    const { id } = state;
    const file = ev.target.files[0];
    if (file && file.size > filesSizeLimit) {
      set({ errorMessage: intl.formatMessage({ id: 'error_filesizeExceeded' }) });
      return;
    }
    set({ errorMessage: '' });
    uploadUserImage(file, id).then(({ id: storage_id, original_name, link, error }) => {
      if (!error) {
        set({ storage_id, original_name, image: link, errorMessage: '' });
      } else {
        const message = getErrorMessageKey(translations, error.code) || error.msg;
        set({ errorMessage: message || 'error uploading image' });
      }
    });
  };

  const removeFile = ev => {
    const { id, storage_id } = state;
    removeUserImage(storage_id, id).then(() => {
      set({ storage_id: null, original_name: null, image: null, errorMessage: '' });
    });
    ev.target.value = '';
  };

  const handleChange = ({ target }) => {
    resetError();
    set({ [target.id]: target.value, errorMessage: '' });
  };

  const handleChangeTags = (field, values) => {
    resetError();
    set({ [field]: values, errorMessage: '' });
  };

  const submit = () => {
    const fields = {};
    Object.keys(profile).forEach((key) => {
      if (profile[key] === undefined || state[key] === null) {
        return; // key doesn't exist or value is null
      }

      // file storage fields, message field
      if (['storage_id', 'image', 'original_name', 'file_storage_params', 'message'].includes(key)) {
        return;
      }

      if ( // "", "abcd", [], ["abcd"], "0", "3"
        typeof state[key] === 'string' || key === 'storage_id' ||
        (typeof state[key] === 'object' && state[key].join('') !== profile[key].join(''))
      ) {
        if (profile[key] !== state[key]) {
          fields[key] = state[key];
        }
      }
    });

    if (!Object.keys(fields).length) {
      set({ errorMessage: 'profile_nothingChanged' });
    } else {
      set({ errorMessage: '' });
      updateAction(fields);
    }
  };

  const {
    image, original_name, storage_id, file_storage_params,
    internal_id, salutation, first_name, last_name, email, supervisor,
    business, location, region, country, city, division, function: userFunction,
    groups, errorMessage, lang, allow_registration, allow_unregistration,
  } = state;

  const { message } = profile;

  return (
    <div className="animated fadeIn mb-3">
      <Row>
        <Col xs="12" md="12">
          <Card className="mb-0">
            <CardHeader><FormattedMessage id="header_profile_profile" /></CardHeader>
            <CardBody>
              <Form action="" method="post" encType="multipart/form-data" className="form-horizontal">
                <Col lg="12" md="12" sm="12" xs="12" className="fl pb-3">
                  <FormGroup row>
                    <Col md="3"><Label htmlFor="image"><FormattedMessage id="profile_photo" /></Label></Col>
                    <Col xs="12" md="9">
                      <Input type="hidden" id="storage_id" value={storage_id || ''} onChange={handleChange} />
                      <Label className="attach_btn fl" color="primary">
                        {(image && original_name) ?
                          <span className="profile_img">
                            <img src={`${image || ''}?w=128&h=128`} alt={original_name || ''} />
                          </span>
                          :
                          <span>
                            <i className="icon-paper-clip" />
                            <Input
                              type="file"
                              onChange={fileUploaded}
                              style={{ position: 'absolute', top: -1000 }}
                              accept={file_storage_params && file_storage_params.allowed_file_formats.map(extension => `.${extension}`).join(', ')}
                              disabled={!permissions.includes('LEARNING_PROFILE_EDIT_PHOTO')}
                            />
                            <FormattedMessage id="profile_attachFile" />
                          </span>}
                      </Label>{(image && original_name) &&
                        <Button
                          color="primary"
                          className="attach_btn"
                          onClick={removeFile}
                          disabled={!permissions.includes('LEARNING_PROFILE_EDIT_PHOTO')}
                        >
                          <i className="icon-close" />
                        </Button>}
                    </Col>
                  </FormGroup>
                  <React.Fragment>
                    <FormGroup row>
                      <Col md="3"><Label htmlFor="internal_id"><FormattedMessage id="profile_internalID" /></Label></Col>
                      <Col xs="12" md="9">
                        <Input
                          type="text"
                          id="internal_id"
                          autoComplete="name"
                          placeholder={intl.formatMessage({ id: 'profile_internalID' })}
                          value={internal_id || ''}
                          onChange={handleChange}
                          disabled={!permissions.includes('LEARNING_PROFILE_EDIT_MAIN_INFO')}
                        />
                      </Col>
                    </FormGroup>
                    <FormGroup row>
                      <Col md="3"><Label htmlFor="salutation"><FormattedMessage id="profile_salutation" /></Label></Col>
                      <Col xs="12" md="9">
                        <Input
                          type="text"
                          id="salutation"
                          autoComplete="name"
                          placeholder={intl.formatMessage({ id: 'profile_salutation' })}
                          value={salutation || ''}
                          onChange={handleChange}
                          disabled={!permissions.includes('LEARNING_PROFILE_EDIT_MAIN_INFO')}
                        />
                      </Col>
                    </FormGroup>
                    <FormGroup row>
                      <Col md="3"><Label htmlFor="first_name"><FormattedMessage id="profile_firstName" /> *</Label></Col>
                      <Col xs="12" md="9">
                        <Input
                          type="text"
                          id="first_name"
                          autoComplete="name"
                          placeholder={intl.formatMessage({ id: 'profile_firstName' })}
                          value={first_name}
                          onChange={handleChange}
                          disabled={!permissions.includes('LEARNING_PROFILE_EDIT_MAIN_INFO')}
                        />
                      </Col>
                    </FormGroup>
                    <FormGroup row>
                      <Col md="3"><Label htmlFor="last_name"><FormattedMessage id="profile_lastName" /> *</Label></Col>
                      <Col xs="12" md="9">
                        <Input
                          type="text"
                          id="last_name"
                          autoComplete="name"
                          placeholder={intl.formatMessage({ id: 'profile_lastName' })}
                          value={last_name}
                          onChange={handleChange}
                          disabled={!permissions.includes('LEARNING_PROFILE_EDIT_MAIN_INFO')}
                        />
                      </Col>
                    </FormGroup>
                  </React.Fragment>
                  <FormGroup row>
                    <Col md="3"><Label htmlFor="email"><FormattedMessage id="profile_email" /> *</Label></Col>
                    <Col xs="12" md="9">
                      <Input
                        type="email"
                        id="email"
                        autoComplete="email"
                        placeholder={intl.formatMessage({ id: 'profile_email' })}
                        value={email}
                        onChange={handleChange}
                        disabled={!permissions.includes('LEARNING_PROFILE_EDIT_SENSITIVE_INFO')}
                      />
                    </Col>
                  </FormGroup>
                  <FormGroup row>
                    <Col md="3"><Label htmlFor="groups"><FormattedMessage id="profile_groups" /></Label></Col>
                    <Col xs="12" md="9">
                      <TagsInput
                        type="text"
                        id="groups"
                        autoComplete="name"
                        inputProps={{ placeholder: intl.formatMessage({ id: 'profile_groups' }) }}
                        value={groups}
                        onlyUnique
                        disabled
                      />
                    </Col>
                  </FormGroup>
                  <FormGroup row>
                    <Col md="3"><Label htmlFor="lang"><FormattedMessage id="profile_lang" /> *</Label></Col>
                    <Col xs="12" md="9">
                      <Input
                        type="text"
                        id="lang"
                        autoComplete="lang"
                        placeholder={intl.formatMessage({ id: 'profile_lang' })}
                        value={intl.formatMessage({ id: lang })}
                        disabled
                      />
                    </Col>
                  </FormGroup>
                </Col>
                <Col lg="12" md="12" sm="12" xs="12" className="fl pb-3">
                  <FormGroup row>
                    <Col md="3"><Label htmlFor="supervisor"><FormattedMessage id="profile_supervisorEmail" /> *</Label></Col>
                    <Col xs="12" md="9">
                      <Input
                        type="text"
                        id="supervisor"
                        autoComplete="name"
                        placeholder={intl.formatMessage({ id: 'profile_supervisorEmail' })}
                        value={supervisor}
                        onChange={handleChange}
                        disabled={!permissions.includes('LEARNING_PROFILE_EDIT_SELECT_SUPERVISOR')}
                      />
                    </Col>
                  </FormGroup>
                  <React.Fragment>
                    <FormGroup row>
                      <Col md="3"><Label htmlFor="business"><FormattedMessage id="profile_business" /></Label></Col>
                      <Col xs="12" md="9">
                        <TagsInput
                          type="text"
                          id="business"
                          autoComplete="name"
                          inputProps={{ placeholder: intl.formatMessage({ id: 'profile_business' }) }}
                          value={business}
                          onChange={values => handleChangeTags('business', values)}
                          onlyUnique
                          addOnBlur
                          disabled={!permissions.includes('LEARNING_PROFILE_EDIT_HASHTAGS')}
                        />
                      </Col>
                    </FormGroup>
                    <FormGroup row>
                      <Col md="3"><Label htmlFor="location"><FormattedMessage id="profile_location" /></Label></Col>
                      <Col xs="12" md="9">
                        <TagsInput
                          type="text"
                          id="location"
                          autoComplete="name"
                          inputProps={{ placeholder: intl.formatMessage({ id: 'profile_location' }) }}
                          value={location}
                          onChange={values => handleChangeTags('location', values)}
                          onlyUnique
                          addOnBlur
                          disabled={!permissions.includes('LEARNING_PROFILE_EDIT_HASHTAGS')}
                        />
                      </Col>
                    </FormGroup>
                    <FormGroup row>
                      <Col md="3"><Label htmlFor="region"><FormattedMessage id="profile_region" /></Label></Col>
                      <Col xs="12" md="9">
                        <TagsInput
                          type="text"
                          id="region"
                          autoComplete="name"
                          inputProps={{ placeholder: intl.formatMessage({ id: 'profile_region' }) }}
                          value={region}
                          onChange={values => handleChangeTags('region', values)}
                          onlyUnique
                          addOnBlur
                          disabled={!permissions.includes('LEARNING_PROFILE_EDIT_HASHTAGS')}
                        />
                      </Col>
                    </FormGroup>
                    <FormGroup row>
                      <Col md="3"><Label htmlFor="country"><FormattedMessage id="profile_country" /></Label></Col>
                      <Col xs="12" md="9">
                        <TagsInput
                          type="text"
                          id="country"
                          autoComplete="name"
                          inputProps={{ placeholder: intl.formatMessage({ id: 'profile_country' }) }}
                          value={country}
                          onChange={values => handleChangeTags('country', values)}
                          onlyUnique
                          addOnBlur
                          disabled={!permissions.includes('LEARNING_PROFILE_EDIT_HASHTAGS')}
                        />
                      </Col>
                    </FormGroup>
                    <FormGroup row>
                      <Col md="3"><Label htmlFor="city"><FormattedMessage id="profile_city" /></Label></Col>
                      <Col xs="12" md="9">
                        <TagsInput
                          type="text"
                          id="city"
                          autoComplete="name"
                          inputProps={{ placeholder: intl.formatMessage({ id: 'profile_city' }) }}
                          value={city}
                          onChange={values => handleChangeTags('city', values)}
                          onlyUnique
                          addOnBlur
                          disabled={!permissions.includes('LEARNING_PROFILE_EDIT_HASHTAGS')}
                        />
                      </Col>
                    </FormGroup>
                    <FormGroup row>
                      <Col md="3"><Label htmlFor="division"><FormattedMessage id="profile_division" /></Label></Col>
                      <Col xs="12" md="9">
                        <TagsInput
                          type="text"
                          id="division"
                          autoComplete="name"
                          inputProps={{ placeholder: intl.formatMessage({ id: 'profile_division' }) }}
                          value={division}
                          onChange={values => handleChangeTags('division', values)}
                          onlyUnique
                          addOnBlur
                          disabled={!permissions.includes('LEARNING_PROFILE_EDIT_HASHTAGS')}
                        />
                      </Col>
                    </FormGroup>
                    <FormGroup row>
                      <Col md="3"><Label htmlFor="function"><FormattedMessage id="profile_function" /></Label></Col>
                      <Col xs="12" md="9">
                        <TagsInput
                          type="text"
                          id="function"
                          autoComplete="name"
                          inputProps={{ placeholder: intl.formatMessage({ id: 'profile_function' }) }}
                          value={userFunction}
                          onChange={values => handleChangeTags('function', values)}
                          onlyUnique
                          addOnBlur
                          disabled={!permissions.includes('LEARNING_PROFILE_EDIT_HASHTAGS')}
                        />
                      </Col>
                    </FormGroup>
                  </React.Fragment>
                </Col>
                <Col xs="12" className="clear">
                  {(errorMessage || message) && <Alert color="danger"><FormattedMessage id={errorMessage || message} /></Alert>}
                </Col>
              </Form>
              <Row>
                <Col className="mb-2" xs="12" md="6">
                  <Button
                    onClick={submit}
                    color="primary"
                    size="lg"
                    disabled={!permissions.includes('LEARNING_PROFILE')}
                  >
                    <FormattedMessage id="overall_input_submit" />
                  </Button>
                </Col>
                <Col className="mb-2" xs="12" md="6">
                  <Unregister allow_registration={allow_registration} allow_unregistration={allow_unregistration} />
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

Profile.propTypes = {
  intl: PropTypes.objectOf(PropTypes.any),
  permissions: PropTypes.arrayOf(PropTypes.string).isRequired,
  profile: PropTypes.objectOf(PropTypes.any),
  translations: PropTypes.objectOf(PropTypes.object).isRequired,
  getAction: PropTypes.func.isRequired,
  updateAction: PropTypes.func.isRequired,
  setError: PropTypes.func.isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(injectIntl(Profile));



// WEBPACK FOOTER //
// ./src/views/_Profile/index.js